import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import {
  LayoutDashboard, Loader2, ClipboardList, Inbox, FileText, Settings,
  Activity, ChevronRight, CheckCircle2, AlertTriangle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DriverWorkspacePage = () => {
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/driver-workspace`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 403) { setAvailable(false); return; }
        if (!res.ok) throw new Error('load failed');
        setData(await res.json());
        setAvailable(true);
      } catch { /* keep prior */ } finally { setLoading(false); }
    })();
  }, []);

  const sections = data?.sections || {};
  const summary = data?.summary || {};

  const tiles = [
    {
      key: 'application_status', to: '/track-application', icon: Activity,
      title: 'Application Status',
      value: summary.application_status_label || '—',
      enabled: sections.application_status?.enabled,
    },
    {
      key: 'onboarding_checklist', to: '/dashboard', icon: ClipboardList,
      title: 'Onboarding',
      value: summary.onboarding_progress
        ? `${summary.onboarding_progress.completed}/${summary.onboarding_progress.total} tasks`
        : '—',
      enabled: sections.onboarding_checklist?.enabled,
    },
    {
      key: 'messages', to: '/messages', icon: Inbox,
      title: 'Messages',
      value: summary.unread_messages != null ? `${summary.unread_messages} unread` : '—',
      enabled: sections.messages?.enabled,
    },
    {
      key: 'documents', to: '/documents-center', icon: FileText,
      title: 'Documents',
      value: summary.documents_missing != null
        ? `${summary.documents_missing} missing`
        : '—',
      enabled: sections.documents?.enabled,
    },
    {
      key: 'communication_preferences', to: '/communication-preferences', icon: Settings,
      title: 'Preferences',
      value: sections.communication_preferences?.enabled ? 'Manage' : '—',
      enabled: sections.communication_preferences?.enabled,
    },
  ];

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="driver-workspace-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <LayoutDashboard className="w-7 h-7 text-blue-600" />
            My Workspace
          </h1>
          <p className="text-gray-600 mt-1">Everything about your onboarding in one place.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12" data-testid="driver-workspace-loading">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : !available ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-testid="driver-workspace-unavailable">
            <p className="text-gray-700">Workspace is not currently available.</p>
          </div>
        ) : (
          <>
            {(summary.documents_expired > 0) && (
              <div
                className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800"
                data-testid="driver-workspace-alert-expired"
              >
                <AlertTriangle className="w-5 h-5" />
                <span>{summary.documents_expired} document(s) expired — please review your documents.</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="driver-workspace-tiles">
              {tiles.map((t) => {
                const Icon = t.icon;
                const disabled = !t.enabled;
                const inner = (
                  <div
                    className={`rounded-xl border p-5 transition-colors ${
                      disabled
                        ? 'bg-gray-50 border-gray-200 opacity-60'
                        : 'bg-white border-gray-200 hover:border-blue-400 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-5 h-5 ${disabled ? 'text-gray-400' : 'text-blue-600'}`} />
                        <span className="font-semibold text-gray-900">{t.title}</span>
                      </div>
                      {!disabled && <ChevronRight className="w-4 h-4 text-gray-400" />}
                    </div>
                    <p className="text-sm text-gray-600 mt-3" data-testid={`driver-workspace-value-${t.key}`}>
                      {disabled ? 'Not available' : t.value}
                    </p>
                  </div>
                );
                return disabled ? (
                  <div key={t.key} data-testid={`driver-workspace-tile-${t.key}`}>{inner}</div>
                ) : (
                  <Link key={t.key} to={t.to} data-testid={`driver-workspace-tile-${t.key}`}>{inner}</Link>
                );
              })}
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="driver-workspace-footer">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              Your workspace updates automatically as your onboarding progresses.
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DriverWorkspacePage;
